import React from 'react';
import { PageType } from '../App';
import UseCaseMMC from '../components/UseCaseMMC';
import HowItWorksSection from '../components/HowItWorksSection';

interface ExecutivePageProps {
  setCurrentPage: (page: PageType) => void;
}

const ExecutivePage: React.FC<ExecutivePageProps> = ({ setCurrentPage }) => {
  return (
    <>
      <section className="uc-hero">
        <div className="uc-hero-inner">
          <span className="uc-hero-label">For CEOs &amp; CFOs</span>
          <h1>Your board will ask hard questions.<br/><em>Have the right answers.</em></h1>
          <p>Every expansion decision lands on your desk. We give you a complete financial model of what actually happens when you enter — true costs, revenue timeline, and three risk scenarios — built on real market data. Not assumptions. Not estimates. A simulation you can defend in the boardroom.</p>
          <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }} className="uc-hero-cta">Start Your Free Simulation</a>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',marginTop:'20px'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:'10px',padding:'12px 16px',background:'rgba(255,255,255,0.05)',borderRadius:'10px',border:'1px solid rgba(255,255,255,0.1)',maxWidth:'400px'}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7c9a6e" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',lineHeight:'1.5',margin:0}}><strong style={{color:'rgba(255,255,255,0.7)',fontWeight:600}}>Your data stays private.</strong> Strictly confidential. Never shared without your permission.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="uc-section">
        <div className="uc-label"><span>Why Leadership Teams Use Market Entry</span></div>
        <h2 className="uc-title">The board doesn't want optimism.<br/><em>They want a model they can stress-test.</em></h2>
        <p className="uc-sub">A confident expansion decision isn't made in a gut feel — it's made when you've seen the conservative scenario, the base case, and the upside, and you still want to go.</p>
        <div className="uc-features">
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
            <h3>Confirm you're entering the right market</h3>
            <p>Is your proposed target actually the best option? We simulate it and automatically compare it against similar markets — ranked by profitability, speed to revenue, and competitive intensity. You either confirm the decision or find a better one.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg></div>
            <h3>Know the true cost before you commit</h3>
            <p>Real local salaries, employer taxes, entity setup costs, and operational benchmarks from companies already in your target market. Not a consultant's estimate — actual data, modelled to your specific team and business.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></div>
            <h3>Three scenarios — no blind spots</h3>
            <p>Conservative, base, and optimistic. See how the expansion performs if customer acquisition is slow, on plan, or ahead of target. Know the downside before you approve the budget, not after.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>
            <h3>Board-ready from day one</h3>
            <p>A full 12-month model with go/no-go verdict, cost breakdown, cashflow projection, and multi-market comparison. The kind of analysis your board expects — without months of internal resource.</p>
          </div>
        </div>
      </div>

      <UseCaseMMC />
      <HowItWorksSection type="executive" />
    </>
  );
};

export default ExecutivePage;
